import type {
  HasilZaka,
  KecerdasanItem,
  LobusKey,
  NilaiTurunan,
} from "@dfa/types";
import type { HuluHasil } from "./hulu.js";

type BobotTerm = { bobot: number; slot: LobusKey };

function L(hulu: HuluHasil, slot: LobusKey): number {
  return hulu.lobus_nilai[slot];
}

function slotTerukur(hulu: HuluHasil, slot: LobusKey): boolean {
  return !hulu.lobus_X.includes(slot);
}

function nilaiBerbobot(
  terms: BobotTerm[],
  hulu: HuluHasil,
): NilaiTurunan {
  const totalBobot = terms.reduce((s, t) => s + t.bobot, 0);
  const bobotTerukur = terms
    .filter((t) => slotTerukur(hulu, t.slot))
    .reduce((s, t) => s + t.bobot, 0);

  if (bobotTerukur === 0) {
    return { nilai: null, bobotTerukur: 0, bandingkan: false };
  }

  const nilai = terms.reduce((s, t) => s + t.bobot * L(hulu, t.slot), 0);
  const bandingkan = Math.abs(bobotTerukur - totalBobot) < 1e-9;

  return { nilai, bobotTerukur: bobotTerukur / totalBobot, bandingkan };
}

const KAPASITAS_TERMS: Record<
  keyof HasilZaka["kapasitas"],
  BobotTerm[]
> = {
  Memory: [
    { bobot: 0.5, slot: "KTem" },
    { bobot: 0.5, slot: "NTem" },
  ],
  Association: [
    { bobot: 0.75, slot: "NFro" },
    { bobot: 0.25, slot: "NPar" },
  ],
  Comprehension: [{ bobot: 1, slot: "KFro" }],
  Judment: [
    { bobot: 0.2, slot: "KPre" },
    { bobot: 0.4, slot: "NPre" },
    { bobot: 0.4, slot: "NFro" },
  ],
  Reasoning: [
    { bobot: 2 / 3, slot: "KFro" },
    { bobot: 1 / 3, slot: "NFro" },
  ],
  Conceptualization: [
    { bobot: 0.5, slot: "NPre" },
    { bobot: 0.5, slot: "NFro" },
  ],
  Volition: [
    { bobot: 0.5, slot: "KPre" },
    { bobot: 0.5, slot: "NPre" },
  ],
  Intuition: [
    { bobot: 1 / 3, slot: "NPre" },
    { bobot: 1 / 3, slot: "NFro" },
    { bobot: 1 / 6, slot: "NTem" },
    { bobot: 1 / 6, slot: "NOcc" },
  ],
  Creativity: [
    { bobot: 0.2, slot: "KFro" },
    { bobot: 0.4, slot: "NFro" },
    { bobot: 0.4, slot: "NTem" },
  ],
};

const PETA_MI: Record<
  keyof HasilZaka["kecerdasan"],
  LobusKey[]
> = {
  "Logical Mathematical": ["KFro"],
  "Verbal Linguistic": ["KTem"],
  "Musical Rhythmic": ["NTem"],
  "Bodily Kinesthetic": ["KPar"],
  "Visual Spatial": ["NPar", "NOcc"],
  "Interpersonal": ["NPre", "NFro"],
  "Naturalist": ["KOcc"],
  Intrapersonal: ["KPre"],
};

const URUTAN_MI: (keyof HasilZaka["kecerdasan"])[] = [
  "Logical Mathematical",
  "Verbal Linguistic",
  "Musical Rhythmic",
  "Bodily Kinesthetic",
  "Visual Spatial",
  "Interpersonal",
  "Naturalist",
  "Intrapersonal",
];

function hitungKecerdasan(
  hulu: HuluHasil,
  /** true = rata-rata lobus (benar); false = jumlah lobus (kontrol negatif mutasi). */
  gunakanRataRata = true,
): HasilZaka["kecerdasan"] {
  const mentah: Partial<Record<keyof HasilZaka["kecerdasan"], number>> = {};
  const meta: Partial<Record<keyof HasilZaka["kecerdasan"], { lobus: number; bobotTerukur: number }>> =
    {};

  for (const nama of URUTAN_MI) {
    const slots = PETA_MI[nama];
    const bobotTerukur =
      slots.filter((s) => slotTerukur(hulu, s)).length / slots.length;
    const sum = slots.reduce((s, slot) => s + L(hulu, slot), 0);
    const raw = gunakanRataRata ? sum / slots.length : sum;
    mentah[nama] = raw;
    meta[nama] = { lobus: slots.length, bobotTerukur };
  }

  const sumMentah = URUTAN_MI.reduce((s, n) => s + (mentah[n] ?? 0), 0);

  const out = {} as HasilZaka["kecerdasan"];
  for (const nama of URUTAN_MI) {
    const { lobus, bobotTerukur } = meta[nama]!;
    if (bobotTerukur === 0 || sumMentah === 0) {
      out[nama] = {
        lobus,
        bobotTerukur: 0,
        pangsa: null,
        simpangan: null,
        bandingkan: false,
      };
      continue;
    }
    const pangsa = ((mentah[nama] ?? 0) / sumMentah) * 100;
    out[nama] = {
      lobus,
      bobotTerukur,
      pangsa,
      simpangan: pangsa - 12.5,
      bandingkan: bobotTerukur === 1,
    };
  }
  return out;
}

function labelBelah(rasional: number, emosional: number): HasilZaka["label_belah"] {
  if (Math.abs(rasional - emosional) <= 2.5) return "Seimbang";
  return rasional > emosional ? "Rasional" : "Emosional";
}

function hitungGayaBerfikir(hulu: HuluHasil): {
  gaya_berfikir: HasilZaka["gaya_berfikir"];
  gaya_berfikir_menang: HasilZaka["gaya_berfikir_menang"];
} {
  const raw = {
    Horizontal: L(hulu, "NTem"),
    Vertical: L(hulu, "NFro"),
    Abstract: L(hulu, "KFro"),
  };
  const s = raw.Horizontal + raw.Vertical + raw.Abstract;
  if (s === 0) {
    return {
      gaya_berfikir: { Horizontal: 0, Vertical: 0, Abstract: 0 },
      gaya_berfikir_menang: "Horizontal",
    };
  }
  const gaya_berfikir = {
    Horizontal: (raw.Horizontal / s) * 100,
    Vertical: (raw.Vertical / s) * 100,
    Abstract: (raw.Abstract / s) * 100,
  };
  const m = Math.max(gaya_berfikir.Horizontal, gaya_berfikir.Vertical, gaya_berfikir.Abstract);
  const seri = (["Vertical", "Abstract", "Horizontal"] as const).filter(
    (k) => Math.abs(gaya_berfikir[k] - m) < 1e-9,
  );
  return { gaya_berfikir, gaya_berfikir_menang: seri[0] ?? "Horizontal" };
}

/** §B — lobus → kapasitas, belah, gaya, kecerdasan (termasuk rambatan X §B1). */
export function hitungTurunan(
  hulu: HuluHasil,
  opts?: { gunakanRataRataMi?: boolean },
): Omit<
  HasilZaka,
  "jariX" | "TNGF" | "lobus_nilai" | "lobus_persen" | "lobus_X" | "karakter" | "karakter_alasan" | "atd"
> {
  const ki =
    L(hulu, "KPre") +
    L(hulu, "KFro") +
    L(hulu, "KPar") +
    L(hulu, "KTem") +
    L(hulu, "KOcc");
  const ka =
    L(hulu, "NPre") +
    L(hulu, "NFro") +
    L(hulu, "NPar") +
    L(hulu, "NTem") +
    L(hulu, "NOcc");
  const rasional = (ki / hulu.TNGF) * 100;
  const emosional = (ka / hulu.TNGF) * 100;

  const kapasitas = {} as HasilZaka["kapasitas"];
  for (const [nama, terms] of Object.entries(KAPASITAS_TERMS) as [
    keyof HasilZaka["kapasitas"],
    BobotTerm[],
  ][]) {
    kapasitas[nama] = nilaiBerbobot(terms, hulu);
  }

  const occ = L(hulu, "KOcc") + L(hulu, "NOcc");
  const tem = L(hulu, "KTem") + L(hulu, "NTem");
  const par = L(hulu, "KPar") + L(hulu, "NPar");
  const sBelajar = occ + tem + par;
  const gaya_belajar =
    sBelajar === 0
      ? { Visual: 0, Auditory: 0, Touch: 0 }
      : {
          Visual: (occ / sBelajar) * 100,
          Auditory: (tem / sBelajar) * 100,
          Touch: (par / sBelajar) * 100,
        };

  const { gaya_berfikir, gaya_berfikir_menang } = hitungGayaBerfikir(hulu);
  const kecerdasan = hitungKecerdasan(hulu, opts?.gunakanRataRataMi !== false);

  return {
    rasional,
    emosional,
    label_belah: labelBelah(rasional, emosional),
    kapasitas,
    gaya_belajar,
    gaya_berfikir,
    gaya_berfikir_menang,
    kecerdasan,
  };
}

export { hitungKecerdasan, PETA_MI, URUTAN_MI };
